<template>
  <v-dialog v-model="internalActive" max-width="600" persistent>
    <v-card v-if="observacion">
      <v-card-title class="text-h6">Seguimientos — {{ observacion.nombrePersonal || 'Observación' }}</v-card-title>
      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate color="secondary" class="mb-2"></v-progress-linear>

        <template v-else>
          <div v-if="seguimientos.length === 0" class="sin-contenido mb-3">Sin seguimientos registrados</div>
          <v-list v-else dense class="transparent mb-3">
            <v-list-item v-for="seguimiento in seguimientos" :key="seguimiento.idSeguimiento">
              <v-list-item-content>
                <v-list-item-title class="seguimiento-texto">{{ seguimiento.seguimiento }}</v-list-item-title>
                <v-list-item-subtitle v-if="seguimiento.fecha">{{ seguimiento.fecha }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="flex-row">
                <v-btn v-if="seguimiento.adjunto" icon small color="info" @click="descargarAdjunto(seguimiento)">
                  <v-icon small>mdi-paperclip</v-icon>
                </v-btn>
                <v-btn icon small color="secondary" @click="abrirFormularioEditar(seguimiento)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="error" @click="abrirDialogoEliminar(seguimiento)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </template>

        <v-btn small text color="secondary" @click="abrirFormularioCrear">
          <v-icon small left>mdi-plus</v-icon>
          Agregar seguimiento
        </v-btn>

        <v-form ref="formSeguimiento" v-model="formularioValido" class="mt-4" v-if="mostrarFormulario">
          <v-textarea
            v-model="textoSeguimiento"
            label="Seguimiento"
            no-resize
            rows="3"
            color="secondary"
            :rules="[(v) => !!v || 'Este campo es requerido.']"
          ></v-textarea>
          <v-file-input
            v-model="archivoAdjunto"
            label="Adjunto (opcional)"
            color="secondary"
            prepend-icon="mdi-paperclip"
            :rules="reglasAdjunto"
            show-size
          ></v-file-input>
          <div class="d-flex justify-end">
            <v-btn text @click="cerrarFormulario">Cancelar</v-btn>
            <v-btn
              color="secondary"
              class="ml-2"
              :loading="loadingFormulario"
              :disabled="!formularioValido"
              @click="guardarSeguimiento"
            >
              Guardar
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cerrar">Cerrar</v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="dialogEliminar" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-h6">Eliminar seguimiento</v-card-title>
        <v-card-text>¿Estás seguro de que deseas eliminar este seguimiento? Esta acción no se puede deshacer.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogEliminar = false">Cancelar</v-btn>
          <v-btn color="error" :loading="loadingEliminar" @click="confirmarEliminacion">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

const TAMANIO_MAXIMO_ADJUNTO = 10 * 1024 * 1024 // 10MB
const EXTENSIONES_PERMITIDAS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx']

export default {
  name: 'SeguimientoObservacionDialogComponent',
  props: {
    active: {
      type: Boolean,
      required: true,
    },
    // Observación puntual resuelta desde el listado de GET .../intervencion/tipo/{type}.
    // Confirmado contra backend (2026-08-01, ver tasks.md 13.7): el endpoint ahora retorna
    // { idResultado, id_personal, nombrePersonal, observacion } — id_personal en snake_case.
    // `tipoObservacion` se agrega client-side por el padre (IntervencionActivaListComponent).
    observacion: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      mostrarFormulario: false,
      formularioValido: false,
      loadingFormulario: false,
      editando: false,
      idSeguimientoEditando: null,
      textoSeguimiento: '',
      archivoAdjunto: null,
      dialogEliminar: false,
      loadingEliminar: false,
      seguimientoAEliminar: null,
      reglasAdjunto: [
        (v) => !v || v.size <= TAMANIO_MAXIMO_ADJUNTO || 'El archivo no debe superar 10MB.',
        (v) => {
          if (!v) return true
          const extension = v.name.split('.').pop().toLowerCase()
          return EXTENSIONES_PERMITIDAS.includes(extension) || 'Tipo de archivo no permitido.'
        },
      ],
    }
  },
  computed: {
    ...mapGetters('seguimientosResultadoC3', {
      isLoadingSeguimientos: 'isLoading',
      getSeguimientosPorTipo: 'getSeguimientosPorTipo',
    }),
    loading() {
      return this.isLoadingSeguimientos
    },
    seguimientos() {
      if (!this.observacion) return []
      return this.getSeguimientosPorTipo(this.observacion.tipoObservacion)
    },
    internalActive: {
      get() {
        return this.active
      },
      set(value) {
        if (!value) this.$emit('close')
      },
    },
  },
  methods: {
    ...mapActions('seguimientosResultadoC3', [
      'fetchSeguimientos',
      'createSeguimiento',
      'updateSeguimiento',
      'deleteSeguimiento',
    ]),
    ...mapMutations(['SET_SNACKBAR_MESSAGE']),

    cerrar() {
      this.$emit('close')
    },

    abrirFormularioCrear() {
      this.editando = false
      this.idSeguimientoEditando = null
      this.textoSeguimiento = ''
      this.archivoAdjunto = null
      this.mostrarFormulario = true
    },

    abrirFormularioEditar(seguimiento) {
      this.editando = true
      this.idSeguimientoEditando = seguimiento.idSeguimiento
      this.textoSeguimiento = seguimiento.seguimiento
      this.archivoAdjunto = null
      this.mostrarFormulario = true
    },

    cerrarFormulario() {
      this.mostrarFormulario = false
      if (this.$refs.formSeguimiento) {
        this.$refs.formSeguimiento.resetValidation()
      }
    },

    async guardarSeguimiento() {
      this.loadingFormulario = true
      try {
        const payload = {
          tipoObservacion: this.observacion.tipoObservacion,
          seguimiento: this.textoSeguimiento,
          adjunto: this.archivoAdjunto || undefined,
        }
        if (this.editando) {
          await this.updateSeguimiento({
            idPersonal: this.observacion.id_personal,
            idResultado: this.observacion.idResultado,
            idSeguimiento: this.idSeguimientoEditando,
            seguimiento: payload,
          })
        } else {
          await this.createSeguimiento({
            idPersonal: this.observacion.id_personal,
            idResultado: this.observacion.idResultado,
            seguimiento: payload,
          })
        }
        this.mostrarFormulario = false
      } catch (error) {
        console.error('[SeguimientoObservacionDialogComponent] guardarSeguimiento failed:', error)
        this.SET_SNACKBAR_MESSAGE(
          this.editando ? 'Ocurrió un error al editar el seguimiento.' : 'Ocurrió un error al crear el seguimiento.'
        )
      } finally {
        this.loadingFormulario = false
      }
    },

    abrirDialogoEliminar(seguimiento) {
      this.seguimientoAEliminar = seguimiento
      this.dialogEliminar = true
    },

    async confirmarEliminacion() {
      this.loadingEliminar = true
      try {
        await this.deleteSeguimiento({
          idPersonal: this.observacion.id_personal,
          idResultado: this.observacion.idResultado,
          idSeguimiento: this.seguimientoAEliminar.idSeguimiento,
        })
        this.dialogEliminar = false
        this.seguimientoAEliminar = null
      } catch (error) {
        console.error('[SeguimientoObservacionDialogComponent] confirmarEliminacion failed:', error)
        this.SET_SNACKBAR_MESSAGE('Ocurrió un error al eliminar el seguimiento.')
      } finally {
        this.loadingEliminar = false
      }
    },

    descargarAdjunto(seguimiento) {
      window.open(seguimiento.adjunto, '_blank')
    },
  },
  watch: {
    observacion: {
      immediate: true,
      async handler(observacion) {
        this.mostrarFormulario = false
        if (observacion && observacion.id_personal && observacion.idResultado) {
          try {
            await this.fetchSeguimientos({ idPersonal: observacion.id_personal, idResultado: observacion.idResultado })
          } catch (error) {
            console.error('[SeguimientoObservacionDialogComponent] fetchSeguimientos failed:', error)
            this.SET_SNACKBAR_MESSAGE('Ocurrió un error al cargar los seguimientos.')
          }
        }
      },
    },
  },
}
</script>

<style scoped>
.seguimiento-texto {
  white-space: pre-line;
}

.sin-contenido {
  color: rgba(0, 0, 0, 0.4);
  font-style: italic;
  font-size: 0.875rem;
}
</style>
